import React, { useState, useEffect, useRef } from 'react';
import { useSearchParams, Link, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { FaCheckCircle, FaTimesCircle, FaSpinner } from 'react-icons/fa';
import { fetchWithAuth } from '../services/api';

const EmailVerification = () => {
  const { t } = useTranslation();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const [verificationStatus, setVerificationStatus] = useState('loading'); // 'loading', 'success', 'error'
  const [message, setMessage] = useState('');
  const verificationInProgress = useRef(false);

  useEffect(() => {
    const token = searchParams.get('token');
    
    if (!token) {
      setVerificationStatus('error');
      setMessage(t('emailVerification.invalidToken') || 'Invalid verification token.');
      return;
    }

    // Prevent multiple simultaneous verification attempts
    if (verificationInProgress.current) {
      return;
    }

    verifyEmail(token);
  }, [searchParams, t]);

  const verifyEmail = async (token) => {
    // Set the flag to prevent duplicate requests
    verificationInProgress.current = true;
    
    try {
      const data = await fetchWithAuth(`/auth/verify?token=${encodeURIComponent(token)}`, {
        method: 'GET',
      });

      if (data.success) {
        // Check if the message indicates already verified (check for specific phrase)
        if (data.message && data.message.toLowerCase().includes('is already verified')) {
          setVerificationStatus('already-verified');
          setMessage(data.message || t('emailVerification.alreadyVerifiedMessage'));
        } else {
          setVerificationStatus('success');
          // Check if the backend message includes "You can now log in" and use appropriate translation
          if (data.message && data.message.toLowerCase().includes('you can now log in')) {
            setMessage(t('emailVerification.verificationSuccessLogin'));
          } else {
            setMessage(data.message || t('emailVerification.verificationSuccess'));
          }
        }
        
        // Remove auto-redirection - let user manually navigate
      } else {
        setVerificationStatus('error');
        setMessage(data.message || t('emailVerification.verificationError'));
      }
    } catch (error) {
      console.error('Email verification error:', error);
      setVerificationStatus('error');
      setMessage(t('emailVerification.verificationError') || 'Verification failed. Please try again.');
    } finally {
      // Clear the flag when done
      verificationInProgress.current = false;
    }
  };

  const currentLang = window.location.pathname.split('/')[1] || 'mk';

  return (
    <div className="min-h-[calc(100vh-4rem)] flex flex-col justify-center py-12 sm:px-6 lg:px-8 bg-gray-50">
      <div className="sm:mx-auto sm:w-full sm:max-w-md">
        <div className="bg-white py-8 px-4 shadow-lg sm:rounded-lg sm:px-10">
          <div className="text-center">
            {verificationStatus === 'loading' && (
              <>
                <div className="mx-auto flex items-center justify-center h-16 w-16 rounded-full bg-blue-100 mb-4">
                  <FaSpinner className="h-8 w-8 text-blue-600 animate-spin" />
                </div>
                <h3 className="text-lg font-semibold text-gray-900 mb-2">
                  {t('emailVerification.verifying') || 'Verifying your email...'}
                </h3>
                <p className="text-sm text-gray-600">
                  {t('emailVerification.pleaseWait') || 'Please wait while we verify your email address.'}
                </p>
              </>
            )}

            {verificationStatus === 'success' && (
              <>
                <div className="mx-auto flex items-center justify-center h-16 w-16 rounded-full bg-green-100 mb-4">
                  <FaCheckCircle className="h-8 w-8 text-green-600" />
                </div>
                <h3 className="text-lg font-semibold text-gray-900 mb-2">
                  {t('emailVerification.successTitle') || 'Email Verified Successfully!'}
                </h3>
                <p className="text-sm text-gray-600 mb-6">
                  {message}
                </p>
                <div className="space-y-3">
                  <Link
                    to={`/${currentLang}/login`}
                    className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 transition-colors duration-200"
                  >
                    {t('emailVerification.proceedToLogin') || 'Proceed to Login'}
                  </Link>
                </div>
              </>
            )}

            {verificationStatus === 'already-verified' && (
              <>
                <div className="mx-auto flex items-center justify-center h-16 w-16 rounded-full bg-blue-100 mb-4">
                  <FaCheckCircle className="h-8 w-8 text-blue-600" />
                </div>
                <h3 className="text-lg font-semibold text-gray-900 mb-2">
                  {t('emailVerification.alreadyVerifiedTitle') || 'Email Already Verified!'}
                </h3>
                <p className="text-sm text-gray-600 mb-6">
                  {message}
                </p>
                <div className="space-y-3">
                  <Link
                    to={`/${currentLang}/login`}
                    className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 transition-colors duration-200"
                  >
                    {t('emailVerification.proceedToLogin') || 'Proceed to Login'}
                  </Link>
                </div>
              </>
            )}

            {verificationStatus === 'error' && (
              <>
                <div className="mx-auto flex items-center justify-center h-16 w-16 rounded-full bg-red-100 mb-4">
                  <FaTimesCircle className="h-8 w-8 text-red-600" />
                </div>
                <h3 className="text-lg font-semibold text-gray-900 mb-2">
                  {t('emailVerification.errorTitle') || 'Verification Failed'}
                </h3>
                <p className="text-sm text-gray-600 mb-6">
                  {message}
                </p>
                <div className="space-y-3">
                  <Link
                    to={`/${currentLang}/register`}
                    className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 transition-colors duration-200"
                  >
                    {t('emailVerification.backToRegister') || 'Back to Registration'}
                  </Link>
                  <div>
                    <Link
                      to={`/${currentLang}/login`}
                      className="text-sm text-red-500 hover:text-red-700 transition-colors duration-200"
                    >
                      {t('emailVerification.backToLogin') || 'Back to Login'}
                    </Link>
                  </div>
                </div>
              </>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default EmailVerification; 